// Encoder ring board assembly (Task 7): the FIRST populated board in the
// project. It takes the shared sensor bands, drops the RAA2P3226 + its tank /
// decoupling passives + an output header just outside the TX allocation, escape-
// routes every chip pin out to its coil terminal / connector / cap, and emits
// BoardDescriptors for both the encoder ring and the passive target ring.
//
// Pure TypeScript. No Angular / DI imports. Deterministic (no Date/random).
// Angles are radians; math frame is y-up. All coil radii come from EncoderBands
// / SensingElement — this module never re-derives a track radius.
//
// ── FACE ASSIGNMENT (documented, normative) ────────────────────────────────
// The ring is 2-layer. Its coil face ('down') points at the rotor's rotating
// target; the component face ('up') points away from the rotor (parts must not
// foul the air gap). Mounting the ring component-side-away-from-rotor makes the
// coil face the BOTTOM copper and the component face the TOP copper, so:
//     'up'   (components + escape routing) -> F.Cu -> Copper,L1,Top
//     'down' (TX/RX coils)                 -> B.Cu -> Copper,L2,Bot
// x2CopperNames is therefore ['Copper,L1,Top','Copper,L2,Bot'] (full FileFunction
// strings, matching statorBoardDescriptor's semantics).
//
// ── VDDD ISOLATION (Task-1 electrical gate) ────────────────────────────────
// CHIP pins 14 (VDD) and 16 (VDDD) both carry net 'VDD' in CHIP.pins, but pin 16
// is an INTERNALLY-REGULATED rail that must NOT be strapped to the pin-14 VDD
// copper. We disambiguate by pin NAME ('VDDD'), not net value: pin 16 escapes to
// its OWN isolated copper island terminating on a dedicated 100 nF cap (C4) to
// GND, and shares no route vertex with any pin-14 VDD copper. A galvanic-
// separation test walks the routed geometry to prove the two islands are
// disjoint.

import { EncoderBands } from './pcb-stator-encoder-bands';
import { ChipSpec, EncoderNet } from './pcb-stator-encoder-chip';
import { MountingHole } from './pcb-stator-mounting';
import { buildSensingElement, CoilTrace } from './pcb-stator-encoder-coils';
import {
  BoardDescriptor, BoardLayerSpec, GerberDraw, DrillSpec,
} from './pcb-stator-gerber';
import { TargetRing } from './pcb-stator-encoder-target';
import { Footprint, FootprintPad, tssop16, passive0603, header254 } from './pcb-stator-footprints';

export interface EncoderBoardParams {
  boreRadiusMm: number;
  traceWidthMm: number;
  clearanceMm: number;
  rxTurns: number;
}

/** THE single source of the DRC-verified encoder fixture (Task 9 review fix):
 *  the ONE full input tuple that has been through the raster DRC
 *  (tools/pcb-stator-drc, `drc:pcb-encoder` / `drc:pcb-target`). Both the DRC
 *  harness's generate.ts (which BUILDS the checked boards from it) and the UI
 *  component's verified-badge fingerprint (which COMPARES against it) import
 *  this constant, so the fixture and the badge can never drift apart. The
 *  encoder boards' copper geometry depends on all of this — not just
 *  EncoderBoardParams: the sensing bands derive from the rotor pocket layout
 *  (stator radii/margins/poles + rotor params) and the hub radius, and the
 *  NPTH ring derives from the mounting config. (targetThicknessMm is NOT part
 *  of the fingerprint: it sets the target disc/recess depth, never any
 *  raster-checked copper.) */
export const VERIFIED_ENCODER_FIXTURE = {
  encoderParams: { boreRadiusMm: 5, traceWidthMm: 0.3, clearanceMm: 0.3, rxTurns: 1 } as EncoderBoardParams,
  hubRadiusMm: 5,
  stator: { innerRadius: 20, outerRadius: 60, innerMargin: 5, outerMargin: 5, numPoles: 8 },
  rotor: {
    magnetShape: 'round' as const,
    magnetThicknessMm: 3, discThicknessMm: 3, boreDiameterMm: 8, pocketClearanceMm: 0.2,
  },
  mounting: { count: 4, holeMm: 3.4, boltRadius: 57.5, startAngleRad: -Math.PI / 2, traceClearance: 0.5 },
};

export interface EncoderBoardResult {
  descriptor: BoardDescriptor;
  placements: Footprint[];
  routes: CoilTrace[];
  valid: boolean;
  reasons: string[];
}

type Pt = [number, number];

// ── Placement constants (documented; all radii clear the TX allocation and sit
//    inside the bolt circle − 6 mm) ─────────────────────────────────────────
const DATUM_RAD = -Math.PI / 2;          // pole-0 datum shared with coils/target
const CHIP_RADIUS_MM = 42;               // chip body center; well outside bands.tx.rOutMm (27.45 at the shipped fixture)
const CHIP_AZIMUTH_RAD = 0;              // chip on +x axis (azimuth 0)
const CAP_OFFSET_MM = 3;                 // radial outboard offset of each flanking cap
const HEADER_RADIUS_MM = 52;             // outer rim, radially outboard of the chip (clear of the caps at r~48)
const HEADER_AZIMUTH_RAD = 0;            // centered on the chip azimuth so the 'down' output fan runs radially without crossing
const CHIP_CLEAR_TO_BOLT_MM = 6;         // chip must stay inside boltCircleR − this
const SILK_STROKE_MM = 0.15;
const ARC_STEP_RAD = 2 * Math.PI / 180;  // escape-arc sample step (keeps chord sag << band margin)
// Neighbouring nets' rings are spaced by exactly `traceWidth+clearance` (the bare
// electrical minimum) center-to-center; two INDEPENDENTLY-sampled concentric arcs
// at that bare spacing sag toward each other by a hair (chord sagitta of a
// polyline approximating a circle bows inward), so the true achieved gap can sit
// a few microns under the nominal spacing. A tiny fixed guard on the RING-TO-RING
// radial step (not on the clearance requirement itself, which stays exact)
// absorbs that sampling artifact deterministically, independent of sample count.
const RING_STEP_GUARD_MM = 0.05;

const polar = (r: number, a: number): Pt => [r * Math.cos(a), r * Math.sin(a)];
const rad = (p: Pt): number => Math.hypot(p[0], p[1]);
const az = (p: Pt): number => Math.atan2(p[1], p[0]);

/** Constant-radius arc from a0 to a1, both endpoints inclusive. Sampled fine
 *  enough that the chord sag never lets an escape route dip from a clear routing
 *  radius into a track band. */
function arcPts(r: number, a0: number, a1: number): Pt[] {
  const span = a1 - a0;
  const n = Math.max(1, Math.ceil(Math.abs(span) / ARC_STEP_RAD));
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) pts.push(polar(r, a0 + (span * i) / n));
  return pts;
}

const ROUTING_VIA_DRILL_MM = 0.3;
const ROUTING_VIA_PAD_MM = 0.6;

/** A short local link (cap <-> chip / cap <-> header): both endpoints already
 *  sit in the clear component annulus (r >> band radii), so a direct 'up'
 *  segment never enters a track band. */
function localLink(from: Pt, to: Pt, net: EncoderNet, widthMm: number): CoilTrace {
  return { net, layer: 'up', points: [from, to], widthMm };
}

/** Header pin order carried out to the connector. */
const HEADER_NETS: EncoderNet[] = ['OUT_A', 'OUT_B', 'OUT_I', 'VDD', 'GND'];

const angDist = (a: number, b: number) => Math.abs(((a - b + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI);

/** Real segment-to-segment distance (identical construction to the (b2)
 *  pairwise-clearance test) instead of hand-derived interval/box math.
 *  Box-based checks (radial range x azimuth range) proved repeatedly bug-prone here: this geometry
 *  mixes radial segments and constant-radius arcs, arcs can sweep in either
 *  direction, and a candidate's own hop is itself an extended obstacle for
 *  OTHER entries' plain radial runs — every one of those interactions needs
 *  its own carefully-signed formula, and each attempt fixed one case while
 *  quietly breaking another. Real coordinates have no direction/sign
 *  ambiguity to get wrong. */
function segSegDistMm(p1: Pt, p2: Pt, p3: Pt, p4: Pt): number {
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  const d1: Pt = [p2[0] - p1[0], p2[1] - p1[1]];
  const d2: Pt = [p4[0] - p3[0], p4[1] - p3[1]];
  const r: Pt = [p1[0] - p3[0], p1[1] - p3[1]];
  const a = d1[0] * d1[0] + d1[1] * d1[1];
  const e = d2[0] * d2[0] + d2[1] * d2[1];
  const f = d2[0] * r[0] + d2[1] * r[1];
  let s: number, t: number;
  if (a <= 1e-12 && e <= 1e-12) { s = 0; t = 0; } else if (a <= 1e-12) {
    s = 0; t = clamp(f / e, 0, 1);
  } else {
    const c = d1[0] * r[0] + d1[1] * r[1];
    if (e <= 1e-12) { t = 0; s = clamp(-c / a, 0, 1); } else {
      const b = d1[0] * d2[0] + d1[1] * d2[1];
      const denom = a * e - b * b;
      s = denom !== 0 ? clamp((b * f - c * e) / denom, 0, 1) : 0;
      t = (b * s + f) / e;
      if (t < 0) { t = 0; s = clamp(-c / a, 0, 1); } else if (t > 1) { t = 1; s = clamp((b - c) / a, 0, 1); }
    }
  }
  const cp1: Pt = [p1[0] + d1[0] * s, p1[1] + d1[1] * s];
  const cp2: Pt = [p3[0] + d2[0] * t, p3[1] + d2[1] * t];
  return Math.hypot(cp1[0] - cp2[0], cp1[1] - cp2[1]);
}

const segPairs = (pts: Pt[]): Array<[Pt, Pt]> => pts.slice(0, -1).map((p, i) => [p, pts[i + 1]] as [Pt, Pt]);

// ── Escape routing v2 (Task-7 fix round 3): breakout-via ladder + joint solve ─
// The round-1/2 per-net greedy azimuth search hit a proven ~7-net joint deadlock
// on 'down' (see task-7-report.md "Fix round 2"). Root cause: a per-net search
// cannot break a mutual deadlock where every net's descent must cross several
// OTHERS' copper to reach open space, and all are simultaneously stuck.
//
// This router dissolves the deadlock with two structural changes:
//   1. LAYER DISCIPLINE. Every azimuthal move (arc) lives on 'down' at a UNIQUE
//      radius; every radial move (stub, descent, terminal hop) lives on 'up'.
//      A radial ('up') and an arc ('down') are on different copper layers, so
//      they can NEVER short — which removes the entire arc-vs-radial hazard the
//      earlier rounds fought. Two arcs are concentric at unique radii (spaced
//      >= traceWidth+clearance+guard) so they never touch. The 'down' layer of
//      the in-family therefore carries ONLY unique-radius arcs, all outside the
//      coil band (r >= arcFloor > bands.tx.rOutMm) — trivially clear of the
//      coils. The mutual 'down' deadlock cannot form because no radial ever
//      lives on 'down'.
//   2. JOINT AZIMUTH SOLVE. What remains is a pure 'up'-layer packing: each
//      net's descent (a radial run at a chosen azimuth) must clear every other
//      net's radial runs AND the RX coils' 'up' crossover bridges. The deep
//      descents can only thread the NARROW azimuth bands between the periodic
//      coil crossover bridges, so this is a genuine joint constraint. We solve
//      it with a bounded, deterministic backtracking search over per-net
//      STATIC-FEASIBLE candidate azimuths (points already clear of coils /
//      bridges / the out-family), most-constrained net first, with forward
//      checking. Arc radii are assigned deep-terminal -> low radius so a deep
//      net's descent is short; the floor keeps the pad->ring stubs (at the pad's
//      native azimuth) from converging below clearance for the dense TSSOP row.
//
// Determinism: sorted keys throughout, fixed candidate grid, first solution
// returned. The (b2) both-layer pairwise clearance test is the final backstop.

// Descent-azimuth search half-window. Must be wide enough that every deep
// terminal has feasible crossing azimuths THREADING BETWEEN the RX coils' 'up'
// crossover bridges AND the relocated RX series-jumper 'up' hops: at ±25° the
// az≈0 cluster (coarse-sin + TX) has no joint solution (proven UNSAT by the
// AC-3 search); at ±45° the solver converges in ~10 nodes. Kept at 45°.
const IN_ARC_RANGE_RAD = 45 * Math.PI / 180;        // descent-azimuth search half-window
const IN_GAP2_RANGE_RAD = 45 * Math.PI / 180;       // two-stage coarse-crossing azimuth half-window
const IN_SEARCH_STEP_RAD = 0.5 * Math.PI / 180;     // candidate grid resolution (shallow nets)
const IN_GAP_STEP_RAD = 1.0 * Math.PI / 180;        // coarser grid for the two-stage deep nets (bounds the 2-D search)
const IN_CAND_GEN = 6000;                            // per-net feasible-candidate generation ceiling
const IN_CAND_CAP = 48;                              // per-net candidates retained after even downsampling (bounds the branching factor + the compat/LCV precompute)
const IN_SOLVE_NODE_CAP = 6_000_000;                // bound on the backtracking search

interface InEscSpec { key: string; net: EncoderNet; pad: Pt; term: Pt; }
interface RBox { net: EncoderNet; a: Pt; b: Pt; rlo: number; rhi: number; }
const rBox = (net: EncoderNet, a: Pt, b: Pt): RBox => ({ net, a, b, rlo: Math.min(rad(a), rad(b)), rhi: Math.max(rad(a), rad(b)) });

/** Real segment-to-segment distance (same construction as segSegDistMm / the
 *  (b2) test) — reused so the router clears copper by exactly the same metric
 *  the pairwise-clearance test asserts. */
const segDist = segSegDistMm;

/** One in-family net's emitted geometry + collision segments. Single-stage
 *  topology (default): 'up' stub (pad -> ring at pad azimuth) -> 'down' transport
 *  arc (ring, padAz -> dAz) -> 'up' descent (ring -> terminal radius at dAz) ->
 *  'up' terminal hop (dAz -> terminal azimuth).
 *
 *  TWO-STAGE (co-design fix): a DEEP terminal (a coarse-band pin whose descent
 *  must cross the whole fine band) can now break its descent at a clear radius
 *  `rGap` in the fine/coarse gap: it crosses the FINE band on 'up' at a free
 *  azimuth `dAz` (chosen to thread the fine coil bridges + the relocated fine
 *  series-jumper hops), transports along a 'down' arc in the gap to a second
 *  azimuth `dAz2`, then crosses the COARSE band on 'up' to its terminal. This
 *  decouples the fine-band crossing azimuth from the coarse terminal azimuth,
 *  which is what makes the joint routing feasible once the RX jumpers add 'up'
 *  copper to the bands. Non-deep nets keep the single-stage topology. */
interface InPlace {
  key: string; net: EncoderNet; dAz: number; dAz2: number; rGap: number;
  traces: CoilTrace[]; vias: Pt[];
  up: Array<[Pt, Pt]>; down: Array<[Pt, Pt]>;
  upBox: Array<[number, number]>; downBox: Array<[number, number]>;
  aabb: [number, number, number, number];   // [minx, miny, maxx, maxy] over all up+down endpoints
}
function buildInPlace(s: InEscSpec, ar: number, dAz: number, w: number, gap?: { rGap: number; dAz2: number }): InPlace {
  const termR = rad(s.term), termAz = az(s.term), padAz = az(s.pad);
  const up: Array<[Pt, Pt]> = [];
  const down: Array<[Pt, Pt]> = [];
  const traces: CoilTrace[] = [];
  const vias: Pt[] = [];
  const entry = polar(ar, padAz);
  const exit = polar(ar, dAz);

  // 'up' stub: pad -> ring at the native pad azimuth.
  up.push([s.pad, entry]);
  traces.push({ net: s.net, layer: 'up', points: [s.pad, entry], widthMm: w });

  // 'down' transport arc: ring, padAz -> dAz (a via at each end carries the
  // layer change). Skipped when the descent already sits at the pad azimuth.
  if (Math.abs(dAz - padAz) > 1e-9) {
    const arc = arcPts(ar, padAz, dAz);
    for (const seg of segPairs(arc)) down.push(seg);
    traces.push({ net: s.net, layer: 'down', points: arc, widthMm: w });
    vias.push(entry, exit);
  }

  if (gap) {
    // Two-stage descent. 'up' descent from the ring down to the gap radius at
    // dAz (crosses the fine band), a 'down' transport arc in the gap to dAz2,
    // then the 'up' descent + terminal hop from the gap radius to the terminal
    // (crosses the coarse band). Vias carry each layer change.
    const gEntry = polar(gap.rGap, dAz), gExit = polar(gap.rGap, gap.dAz2);
    const seg1: Pt[] = [exit, gEntry];
    for (const seg of segPairs(seg1)) up.push(seg);
    traces.push({ net: s.net, layer: 'up', points: seg1, widthMm: w });
    vias.push(exit, gEntry);
    if (Math.abs(gap.dAz2 - dAz) > 1e-9) {
      const gArc = arcPts(gap.rGap, dAz, gap.dAz2);
      for (const seg of segPairs(gArc)) down.push(seg);
      traces.push({ net: s.net, layer: 'down', points: gArc, widthMm: w });
      vias.push(gExit);
    }
    const descPts: Pt[] = [gExit, polar(termR, gap.dAz2)];
    if (Math.abs(gap.dAz2 - termAz) > 1e-9) for (const q of arcPts(termR, gap.dAz2, termAz).slice(1)) descPts.push(q);
    descPts[descPts.length - 1] = s.term;
    for (const seg of segPairs(descPts)) up.push(seg);
    traces.push({ net: s.net, layer: 'up', points: descPts, widthMm: w });
  } else {
    // 'up' descent + terminal hop, one polyline ending EXACTLY on the terminal
    // (so the continuity walk chains pad -> terminal).
    const descPts: Pt[] = [exit, polar(termR, dAz)];
    if (Math.abs(dAz - termAz) > 1e-9) {
      for (const q of arcPts(termR, dAz, termAz).slice(1)) descPts.push(q);
    }
    descPts[descPts.length - 1] = s.term;             // snap to the real terminal vertex
    for (const seg of segPairs(descPts)) up.push(seg);
    traces.push({ net: s.net, layer: 'up', points: descPts, widthMm: w });
  }

  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const segs of [up, down]) for (const [a, b] of segs) for (const q of [a, b]) {
    if (q[0] < minx) minx = q[0]; if (q[0] > maxx) maxx = q[0];
    if (q[1] < miny) miny = q[1]; if (q[1] > maxy) maxy = q[1];
  }
  return {
    key: s.key, net: s.net, dAz, dAz2: gap ? gap.dAz2 : dAz, rGap: gap ? gap.rGap : 0, traces, vias, up, down,
    upBox: up.map(([a, b]) => [Math.min(rad(a), rad(b)), Math.max(rad(a), rad(b))] as [number, number]),
    downBox: down.map(([a, b]) => [Math.min(rad(a), rad(b)), Math.max(rad(a), rad(b))] as [number, number]),
    aabb: [minx, miny, maxx, maxy],
  };
}

/** Escape-route the whole 'in' family (chip pins -> coil terminals) with the
 *  breakout-via ladder above. `upObs`/`downObs` are the FIXED obstacles on each
 *  layer (RX coil 'up' crossover bridges + out-family 'up' routes; RX/TX coil
 *  'down' traces + out-family 'down' routes + cap-GND star). Returns emitted
 *  traces + vias. Throws nothing; if the bounded search is exhausted for a net
 *  (should not happen for the shipped fixtures) it falls back to that net's
 *  first static-feasible azimuth and RETURNS A REASON so the caller can mark the
 *  board invalid — a silent fallback could stamp shorted gerbers `valid:true`. */
function routeInFamily(
  specs: InEscSpec[], upObs: RBox[], downObs: RBox[],
  txROutMm: number, w: number, clearanceMm: number,
  gapBand: { fineRInMm: number; coarseROutMm: number },
  nodeCap: number = IN_SOLVE_NODE_CAP,
): { routes: CoilTrace[]; vias: Pt[]; reasons: string[] } {
  const step = w + clearanceMm;
  const minMm = step;
  // Against the FIXED obstacles (coil copper + already-emitted out/cap routes)
  // require a slightly larger gap than the bare traceWidth+clearance. The solver
  // works in exact geometry but the DRC gate rasterises to a 31 µm/px mask, and a
  // descent that clears a sampled COIL edge by exactly clearanceMm in real
  // coordinates can lose ~1-2 px to rasterisation and read under 0.3 mm (measured:
  // a 0.315 mm descent-vs-coil gap rastered to 0.265 mm). This guard applies ONLY
  // to the static candidate filter — the joint route-vs-route packing still solves
  // at the bare `minMm`, so feasibility is preserved (a uniform guard on the whole
  // solve is UNSAT here) while every route-vs-coil gap stays raster-safe.
  const IN_RASTER_GUARD_MM = 0.06;
  const minStaticMm = step + IN_RASTER_GUARD_MM;
  const arStep = step + RING_STEP_GUARD_MM;

  // Deep (coarse-band) terminals cross the whole fine band; give them the
  // two-stage descent, breaking at a clear radius in the fine/coarse gap. Two
  // gap radii (spaced by the clearance grid) let two nets converging on the
  // same coarse terminal azimuth (each track's SIN_N/SIN_P pair) transport in
  // the gap without their arcs colliding.
  // A net gets the two-stage descent when its terminal sits at or below the
  // fine band's inner edge: it then crosses (nearly) the whole fine 'up' bridge
  // field, so decoupling its fine-crossing azimuth from its terminal azimuth
  // (via a transport arc in the fine/coarse gap) is what buys the joint solve
  // enough freedom to route around the relocated RX series-jumper hops. This
  // covers all four coarse pins AND the two fine INNER pins (SIN_N/COS_N, whose
  // terminals sit on the fine inner edge). The fine OUTER pins barely enter the
  // band and stay single-stage.
  const deepBelowMm = gapBand.fineRInMm + 2;
  const isDeep = (s: InEscSpec): boolean => rad(s.term) < deepBelowMm;
  // Gap-arc radii must clear the coarse band below and the fine band above by
  // the copper clearance (w/2 + clearance), NOT the ring step — the fine/coarse
  // gap is only ~1.5 mm, and using the tighter (correct) margin fits TWO arc
  // radii so both of a track's deep pins (SIN_N and SIN_P, converging on the
  // same coarse terminal azimuth) can transport in the gap on distinct radii.
  const gapMargin = w / 2 + clearanceMm;
  const gapLo = gapBand.coarseROutMm + gapMargin, gapHi = gapBand.fineRInMm - gapMargin;
  const gapRadii = gapHi - gapLo >= step - 1e-9 ? [gapLo, gapHi] : [(gapLo + gapHi) / 2];

  // Arc-radius floor: two stubs at the pad's native azimuths converge as they
  // run inward; keep every arc radius above the point where the tightest
  // same-row pad pitch still clears (plus stay outside the coil band).
  let minRowGap = Infinity;
  for (let i = 0; i < specs.length; i++) {
    for (let j = i + 1; j < specs.length; j++) {
      if (Math.abs(rad(specs[i].pad) - rad(specs[j].pad)) < 2 * arStep) {
        minRowGap = Math.min(minRowGap, angDist(az(specs[i].pad), az(specs[j].pad)));
      }
    }
  }
  const arFloor = Math.max(txROutMm + step, minRowGap > 1e-6 && isFinite(minRowGap) ? (step * 1.02) / minRowGap : 0);

  // Deep terminal -> low arc radius (short descent, fewer bands to thread).
  const byDepth = [...specs].sort((a, b) => rad(a.term) - rad(b.term) || a.key.localeCompare(b.key));
  const arByKey = new Map<string, number>();
  byDepth.forEach((s, i) => arByKey.set(s.key, arFloor + i * arStep));

  // Radial bin index over the fixed obstacles: a candidate segment only clears
  // its radial neighbourhood, so a transport arc at r >= arcFloor never even
  // looks at the thousands of coil-band boxes at r <= tx.rOut. This is what
  // keeps per-net candidate generation (build + clearOf over every combo) fast
  // enough for the two-stage deep search; it is a pure lookup accelerator and
  // does not change which candidates pass (identical geometry + threshold).
  const R_BIN_MM = 1.0;
  const R_INDEX_MAX_MM = 70;                     // covers the full board radius + margin
  const nBins = Math.ceil(R_INDEX_MAX_MM / R_BIN_MM);
  const binOf = (r: number) => Math.max(0, Math.min(nBins - 1, Math.floor(r / R_BIN_MM)));
  const indexObs = (obs: RBox[]): RBox[][] => {
    const bins: RBox[][] = Array.from({ length: nBins }, () => []);
    for (const o of obs) {
      const b0 = binOf(o.rlo - minMm), b1 = binOf(o.rhi + minMm);
      for (let b = b0; b <= b1; b++) bins[b].push(o);
    }
    return bins;
  };
  const upBins = indexObs(upObs), downBins = indexObs(downObs);
  // Per-query dedup so an obstacle spanning several bins is tested once.
  let queryGen = 0;
  const seenGen = new Map<RBox, number>();
  const clearSeg = (bins: RBox[][], net: EncoderNet, seg: [Pt, Pt], box: [number, number]): boolean => {
    const [lo, hi] = box;
    const b0 = binOf(lo - minStaticMm), b1 = binOf(hi + minStaticMm);
    const g = ++queryGen;
    for (let b = b0; b <= b1; b++) {
      const list = bins[b];
      for (let i = 0; i < list.length; i++) {
        const o = list[i];
        if (o.net === net) continue;
        if (seenGen.get(o) === g) continue;
        seenGen.set(o, g);
        if (hi < o.rlo - minStaticMm || lo > o.rhi + minStaticMm) continue;
        if (segDist(o.a, o.b, seg[0], seg[1]) < minStaticMm - 1e-9) return false;
      }
    }
    return true;
  };
  const clearOf = (pl: InPlace): boolean => {
    for (let k = 0; k < pl.up.length; k++) if (!clearSeg(upBins, pl.net, pl.up[k], pl.upBox[k])) return false;
    for (let k = 0; k < pl.down.length; k++) if (!clearSeg(downBins, pl.net, pl.down[k], pl.downBox[k])) return false;
    return true;
  };
  const clearOfPlaced = (pl: InPlace, placed: InPlace[]): boolean => {
    for (const q of placed) {
      if (q.net === pl.net) continue;
      for (let a = 0; a < q.up.length; a++) for (let b = 0; b < pl.up.length; b++) {
        if (segDist(q.up[a][0], q.up[a][1], pl.up[b][0], pl.up[b][1]) < minMm - 1e-9) return false;
      }
      for (let a = 0; a < q.down.length; a++) for (let b = 0; b < pl.down.length; b++) {
        if (segDist(q.down[a][0], q.down[a][1], pl.down[b][0], pl.down[b][1]) < minMm - 1e-9) return false;
      }
    }
    return true;
  };

  // Per-net STATIC-feasible candidate placements (clear of the fixed obstacles),
  // azimuths ordered near-terminal-first. Restricting to a bounded window and to
  // feasible-only points is what keeps the search small and fast.
  // Even downsample to `cap` while preserving list order (a spread across the
  // enumerated azimuth range, not a near-terminal cluster) — keeps the per-net
  // candidate lists small so forward-checking stays cheap, without collapsing
  // the azimuth diversity the joint solve needs.
  const downsample = (list: InPlace[], cap: number): InPlace[] => {
    if (list.length <= cap) return list;
    const out: InPlace[] = [];
    for (let i = 0; i < cap; i++) out.push(list[Math.floor((i * list.length) / cap)]);
    return out;
  };
  const cands = new Map<string, InPlace[]>();
  for (const s of specs) {
    const ar = arByKey.get(s.key)!;
    const termAz = az(s.term);
    const list: InPlace[] = [];
    if (isDeep(s)) {
      // Two-stage: fine-crossing azimuth `d1` free over the full window, second-
      // stage azimuth `d2` around the terminal, gap radius from the small pool.
      // Enumerated near-terminal-first on both axes, then downsampled to spread
      // the retained candidates across the whole window.
      for (let a1 = 0; a1 <= IN_ARC_RANGE_RAD + 1e-9 && list.length < IN_CAND_GEN; a1 += IN_GAP_STEP_RAD) {
        for (const d1 of a1 === 0 ? [termAz] : [termAz + a1, termAz - a1]) {
          for (let a2 = 0; a2 <= IN_GAP2_RANGE_RAD + 1e-9 && list.length < IN_CAND_GEN; a2 += IN_GAP_STEP_RAD) {
            for (const d2 of a2 === 0 ? [termAz] : [termAz + a2, termAz - a2]) {
              for (const rGap of gapRadii) {
                const pl = buildInPlace(s, ar, d1, w, { rGap, dAz2: d2 });
                if (clearOf(pl)) list.push(pl);
              }
            }
          }
        }
      }
    } else {
      for (let d = 0; d <= IN_ARC_RANGE_RAD + 1e-9; d += IN_SEARCH_STEP_RAD) {
        for (const cand of d === 0 ? [termAz] : [termAz + d, termAz - d]) {
          const pl = buildInPlace(s, ar, cand, w);
          if (clearOf(pl)) list.push(pl);
        }
      }
    }
    cands.set(s.key, downsample(list, IN_CAND_CAP));
  }

  // ── Compatibility table + MAC (maintain-arc-consistency) search ─────────────
  // The naive forward check re-scanned every remaining net's candidate list
  // against the placed set at every node — thousands of segment-distance calls
  // per node, which dominated the solve. Instead build, ONCE, a compatibility
  // bitmask: compat[i][a][j] has bit b set iff candidate a of net i and candidate
  // b of net j clear each other. An AABB pre-filter (candidates whose inflated
  // bounding boxes are disjoint cannot collide) keeps the O(nets²·cand²) build
  // affordable even at a high candidate cap — most cross-net candidate pairs are
  // azimuthally separated. The search then propagates full arc consistency (AC-3)
  // through bigint ANDs, so it converges in a handful of nodes, OR proves the
  // candidate space UNSAT by emptying a domain. Fully deterministic (variable
  // order + candidate order are sorted).
  //
  // Variable order: most-constrained net first (fewest candidates), tie deeper-
  // terminal-first. Candidate (value) order per net: least-constraining-value —
  // the placement ruling out the fewest of the other nets' candidates first. LCV
  // is a permutation over candidate indices so the compat bitmasks stay valid.
  const order = [...specs].sort((a, b) =>
    cands.get(a.key)!.length - cands.get(b.key)!.length
    || rad(a.term) - rad(b.term) || a.key.localeCompare(b.key));
  const nOrd = order.length;
  const C = order.map(s => cands.get(s.key)!);
  const bit = (b: number) => 1n << BigInt(b);
  const popcount = (x: bigint): number => { let c = 0; while (x > 0n) { x &= x - 1n; c++; } return c; };
  const full = C.map(c => (1n << BigInt(c.length)) - 1n);
  const disjoint = (p: InPlace, q: InPlace): boolean =>
    p.aabb[2] + minMm < q.aabb[0] || q.aabb[2] + minMm < p.aabb[0] ||
    p.aabb[3] + minMm < q.aabb[1] || q.aabb[3] + minMm < p.aabb[1];
  const compat: bigint[][][] = [];
  for (let i = 0; i < nOrd; i++) {
    const rows: bigint[][] = [];
    for (let a = 0; a < C[i].length; a++) {
      const pa = C[i][a];
      const row: bigint[] = [];
      for (let j = 0; j < nOrd; j++) {
        if (j === i) { row.push(0n); continue; }
        let m = 0n;
        for (let b = 0; b < C[j].length; b++) if (disjoint(pa, C[j][b]) || clearOfPlaced(C[j][b], [pa])) m |= bit(b);
        row.push(m);
      }
      rows.push(row);
    }
    compat.push(rows);
  }
  const perm: number[][] = [];
  for (let i = 0; i < nOrd; i++) {
    const blocked = (a: number) => { let s = 0; for (let j = 0; j < nOrd; j++) if (j !== i) s += C[j].length - popcount(compat[i][a][j]); return s; };
    const score = C[i].map((_, a) => blocked(a));
    perm.push(C[i].map((_, a) => a).sort((x, y) => score[x] - score[y] || x - y));
  }

  // AC-3: prune every domain to arc consistency (drop any value with no support
  // in some other net's live domain), iterating to a fixpoint. Emptying a domain
  // ⇒ the candidate space is UNSAT for the remaining assignment (no search
  // needed). Run at the root and after every tentative assignment (MAC).
  const ac3 = (dom: bigint[]): boolean => {
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < nOrd; i++) {
        let di = dom[i];
        for (let a = 0; a < C[i].length; a++) {
          if (!(di & bit(a))) continue;
          for (let j = 0; j < nOrd; j++) {
            if (j === i) continue;
            if ((compat[i][a][j] & dom[j]) === 0n) { di &= ~bit(a); changed = true; break; }
          }
        }
        if (di !== dom[i]) { dom[i] = di; if (di === 0n) return false; }
      }
    }
    return true;
  };

  let nodes = 0;
  const assign: number[] = new Array(nOrd).fill(-1);
  let solIdx: number[] | null = null;
  const search = (idx: number, domains: bigint[]): boolean => {
    if (idx === nOrd) { solIdx = assign.slice(); return true; }
    for (const a of perm[idx]) {
      if (!(domains[idx] & bit(a))) continue;
      if (++nodes > nodeCap) return false;
      const nd = domains.slice();
      nd[idx] = bit(a);
      if (ac3(nd)) { assign[idx] = a; if (search(idx + 1, nd)) return true; assign[idx] = -1; }
    }
    return false;
  };
  const root = full.slice();
  if (ac3(root)) search(0, root);

  const solution: InPlace[] | null = solIdx ? order.map((s, i) => C[i][solIdx![i]]) : null;
  const byKey = new Map<string, InPlace>();
  if (solution) for (const pl of solution) byKey.set(pl.key, pl);

  // Fallback guard: if the backtracking solver returned no solution (node cap or
  // a net with an empty static-feasible candidate list), the per-net first-
  // feasible / padAz emit below is NOT a verified conflict-free routing — it can
  // leave shorted copper. Surface it as a reason so buildEncoderBoard sets
  // valid:false rather than silently stamping shorted gerbers valid:true.
  const reasons: string[] = [];
  if (!solution) {
    const starved = specs.filter(s => cands.get(s.key)!.length === 0).map(s => String(s.net));
    reasons.push(
      `In-family escape routing did not converge: the bounded backtracking solver found no `
      + `conflict-free azimuth assignment (node cap ${nodeCap}`
      + (starved.length ? `; nets with no static-feasible candidate: ${starved.join(', ')}` : '')
      + `). Fell back to per-net first-feasible azimuths — the emitted routing may short `
      + `and is NOT fabrication-clean.`);
  }

  const routes: CoilTrace[] = [];
  const vias: Pt[] = [];
  for (const s of specs) {                            // deterministic emit order
    const pl = byKey.get(s.key)
      ?? cands.get(s.key)![0]                          // search exhausted: first feasible (backstop: reason above + (b2) test)
      ?? buildInPlace(s, arByKey.get(s.key)!, az(s.term), w);
    routes.push(...pl.traces);
    vias.push(...pl.vias);
  }
  return { routes, vias, reasons };
}

/** Build the fully-populated 2-layer encoder ring board. `opts.inSolveNodeCap`
 *  is a test seam to force the in-family solver's fallback path (see
 *  routeInFamily); it defaults to the production node cap. */
export function buildEncoderBoard(
  bands: EncoderBands, chip: ChipSpec, mounting: MountingHole[], p: EncoderBoardParams,
  opts: { inSolveNodeCap?: number } = {},
): EncoderBoardResult {
  const reasons: string[] = [];
  const w = p.traceWidthMm;

  if (!bands.valid) reasons.push(`Encoder bands are invalid: ${bands.reasons.join('; ') || 'unknown reason'}.`);
  if (mounting.length === 0) {
    reasons.push('No mounting holes: coaxial mounting to the stator requires the same bolt circle; mountingHoles === 0 leaves the ring unlocatable.');
  }

  const boltCircleR = mounting.length ? rad([mounting[0].x, mounting[0].y]) : 0;

  // ── Sensor element (coils) — periods + datum are fixed by the chip/design ──
  const periods = { fine: chip.finePeriodsAllowed[0], coarse: chip.coarsePeriods };
  const se = buildSensingElement(bands, periods, {
    traceWidthMm: w, clearanceMm: p.clearanceMm, rxTurns: p.rxTurns, datumRad: DATUM_RAD,
  });
  const terminalOf = (net: EncoderNet): Pt | null => se.terminals.find(t => t.net === net)?.at ?? null;

  // ── Placement ──────────────────────────────────────────────────────────────
  const chipCenter = polar(CHIP_RADIUS_MM, CHIP_AZIMUTH_RAD);
  const netByPin = (pin: number): EncoderNet => chip.pins.find(pp => pp.n === pin)!.net;
  const chipFp = tssop16(chipCenter, CHIP_AZIMUTH_RAD, netByPin);
  const padOfPin = (pin: number): Pt => chipFp.pads[pin - 1].at;

  // Flank each TX / supply pin with its cap, offset radially outboard so the
  // signal pad (pad 0) sits nearest the chip and the GND pad (pad 1) outboard.
  const capAt = (pin: number): Pt => [padOfPin(pin)[0] + CAP_OFFSET_MM, padOfPin(pin)[1]];
  const cTx1 = passive0603(capAt(9), 0, ['TX_P', 'GND'], 'C1');   // tank split cap on TX_P
  const cTx2 = passive0603(capAt(11), 0, ['TX_N', 'GND'], 'C2');  // tank split cap on TX_N
  const cVdd = passive0603(capAt(14), 0, ['VDD', 'GND'], 'C3');   // VDD decoupling
  const cVddd = passive0603(capAt(16), 0, ['VDD', 'GND'], 'C4');  // VDDD isolated-island decoupling (DEVIATION #3)

  const header = header254(polar(HEADER_RADIUS_MM, HEADER_AZIMUTH_RAD), HEADER_AZIMUTH_RAD + Math.PI / 2, HEADER_NETS, 'J1');
  const headerPad = (net: EncoderNet): Pt => header.pads[HEADER_NETS.indexOf(net)].at;

  const placements: Footprint[] = [chipFp, cTx1, cTx2, cVdd, cVddd, header];

  // ── Escape routing ─────────────────────────────────────────────────────────
  // 'in'  nets reach a coil terminal (radius < chip); 'out' nets reach the
  //  header / an isolated cap (radius >= chip). Ring radii are assigned per net
  //  in deterministic net-name order at the clearance grid.
  interface Spec { key: string; net: EncoderNet; from: Pt; to: Pt; family: 'in' | 'out'; }
  const specs: Spec[] = [];
  for (let pin = 1; pin <= 16; pin++) {
    const net = netByPin(pin);
    const from = padOfPin(pin);
    if (pin === 16) {
      // VDDD — its own island: a DIRECT local link to C4's signal pad only (never
      // pin-14 VDD), emitted below with the other cap signal links. C4's signal
      // pad sits just outboard of pin 16 in the clear component annulus, so the
      // link is a short radial hop that never enters a coil band OR needs the
      // out-family ring detour — routing it as a ring escape (out to r>=51 and
      // back to C4's inboard signal pad) would drag it across C4's own outboard
      // GND pad, an avoidable {VDDD,GND} short. Handled as a link, not a spec.
    } else if (net === 'VDD') {
      specs.push({ key: 'VDD', net, from, to: headerPad('VDD'), family: 'out' });
    } else if (net === 'GND') {
      specs.push({ key: 'GND', net, from, to: headerPad('GND'), family: 'out' });
    } else if (net === 'OUT_A' || net === 'OUT_B' || net === 'OUT_I') {
      specs.push({ key: net, net, from, to: headerPad(net), family: 'out' });
    } else {
      const term = terminalOf(net);
      if (term) specs.push({ key: net, net, from, to: term, family: 'in' });
    }
  }

  const step = w + p.clearanceMm;
  const inSpecsByKey = specs.filter(s => s.family === 'in').sort((a, b) => a.key.localeCompare(b.key));
  const outSpecsByKey = specs.filter(s => s.family === 'out').sort((a, b) => a.key.localeCompare(b.key));

  // ── Escape routing ─────────────────────────────────────────────────────────
  // 'out' family (supply/output pins -> header / isolated cap, all radially
  // OUTSIDE every coil band) fans directly to the header on 'down' (see below)
  // — sparse, shares no radius with any coil, and has always cleared. 'in'
  // family (RX/TX pins -> coil terminals) uses the breakout-via ladder + joint
  // solver (see routeInFamily). The 'in' solve treats every already-emitted
  // 'out'/cap route and every coil trace as a fixed obstacle, so the two
  // families and the coils can never short across the shared chip region.
  const routes: CoilTrace[] = [];
  const routingVias: Pt[] = [];

  // ---- 'out' family (direct 'down' fan to the header) ----
  // The supply/output pins escape to the header on the BOTTOM layer, straight
  // from each chip pad (a via at the pad; the header pins are PTH so no via is
  // needed there). This deliberately avoids the old 'up' ring-ladder: an 'up'
  // stub from the outer chip row runs radially THROUGH the flanking caps' pads
  // (all 'up' SMD copper), which shorts across them — the caps sit right on the
  // escape corridor and the 0.65 mm pin pitch leaves no room to jog an 'up' stub
  // clear of every one. On 'down' the corridor is empty (coils stop well inside,
  // the 'in'-family transport arcs sit at their own radii and are added as
  // obstacles) so the pins reach the header cleanly, and the caps' 'up' pads can
  // never short a 'down' route. The header net order mirrors the pin order, so
  // the five routes fan monotonically without crossing; the small stagger keeps
  // the near-chip spacing (where the fan is tightest) above clearance.
  interface OutEntry { key: string; net: EncoderNet; from: Pt; to: Pt; }
  const outEntries: OutEntry[] = outSpecsByKey.map(s => ({ key: s.key, net: s.net, from: s.from, to: s.to }));
  const capGndTarget = headerPad('GND');
  for (const e of outEntries) {
    routingVias.push(e.from);                   // via at the chip pad: 'up' SMD pad -> 'down' fan
    routes.push({ net: e.net, layer: 'down', points: [e.from, e.to], widthMm: w });
  }

  // The 4 cap GND pads join in a CHAIN along the cap row (all on 'up', all one
  // net) and only the cap nearest the header links out to the header GND pad. A
  // star of four separate links to the corner GND pad would each sweep across the
  // header pad row (grazing the VDD/OUT header pins); the chain keeps all but one
  // segment local to the cap row (r ~ 48, clear of the 'down' output fan below and
  // the header pads at r ~ 52 above), and the single out-link runs from the
  // +azimuth end cap straight to the +azimuth-end GND pad without crossing the
  // intervening pads. No vias: SMD cap pads and the PTH header are both on 'up'.
  const capGnds = [cTx1, cTx2, cVdd, cVddd].map(cap => cap.pads[1].at);  // ordered by azimuth
  for (let i = 0; i < capGnds.length - 1; i++) routes.push({ net: 'GND', layer: 'up', points: [capGnds[i], capGnds[i + 1]], widthMm: w });
  routes.push({ net: 'GND', layer: 'up', points: [capGnds[capGnds.length - 1], capGndTarget], widthMm: w });

  // Cap signal pads tie into their flanked chip pin (short 'up' links in the
  // clear component annulus). Emitted here so they are also fixed obstacles for
  // the 'in' solve below.
  routes.push(localLink(cTx1.pads[0].at, padOfPin(9), 'TX_P', w));
  routes.push(localLink(cTx2.pads[0].at, padOfPin(11), 'TX_N', w));
  routes.push(localLink(cVdd.pads[0].at, padOfPin(14), 'VDD', w));
  // VDDD island: pin 16 -> C4 signal pad ONLY (net value 'VDD', the internally-
  // regulated rail; disambiguated from pin-14 VDD by pin/refdes in the seed map).
  routes.push(localLink(cVddd.pads[0].at, padOfPin(16), 'VDD', w));

  // ---- 'in' family (breakout-via ladder + joint solve) ----
  // Fixed obstacles: coil copper on each layer (RX 'up' crossover bridges + all
  // 'down' coil traces) plus every 'out'/cap route emitted above (both layers).
  const upObs: RBox[] = [];
  const downObs: RBox[] = [];
  for (const t of se.traces) for (const seg of segPairs(t.points)) (t.layer === 'up' ? upObs : downObs).push(rBox(t.net, seg[0], seg[1]));
  for (const r of routes) for (const seg of segPairs(r.points)) (r.layer === 'up' ? upObs : downObs).push(rBox(r.net, seg[0], seg[1]));
  const inEsc = routeInFamily(
    inSpecsByKey.map(s => ({ key: s.key, net: s.net, pad: s.from, term: s.to })),
    upObs, downObs, bands.tx.rOutMm, w, p.clearanceMm,
    { fineRInMm: bands.fine.rInMm, coarseROutMm: bands.coarse.rOutMm }, opts.inSolveNodeCap,
  );
  routes.push(...inEsc.routes);
  routingVias.push(...inEsc.vias);
  reasons.push(...inEsc.reasons);   // fallback guard: any solver non-convergence invalidates the board

  // ── Chip placement bound check (test d) ─────────────────────────────────────
  const chipRadii = chipFp.pads.map(pd => rad(pd.at));
  const rMin = Math.min(...chipRadii), rMax = Math.max(...chipRadii);
  if (rMin <= bands.tx.rOutMm) {
    reasons.push(`Chip encroaches the TX allocation: nearest pad R${rMin.toFixed(2)}mm <= bands.tx.rOutMm R${bands.tx.rOutMm.toFixed(2)}mm.`);
  }
  if (boltCircleR > 0 && rMax >= boltCircleR - CHIP_CLEAR_TO_BOLT_MM) {
    reasons.push(`Chip too far out: farthest pad R${rMax.toFixed(2)}mm >= bolt circle R${boltCircleR.toFixed(2)}mm − ${CHIP_CLEAR_TO_BOLT_MM}mm.`);
  }

  const descriptor = assembleDescriptor(bands, se, placements, routes, routingVias, mounting, p);
  return { descriptor, placements, routes, valid: reasons.length === 0, reasons };
}

// ── Descriptor assembly ──────────────────────────────────────────────────────

const OUTLINE_SAMPLES = 128;
const ENCODER_OD_MM = 120;                 // annulus outer diameter
const ENCODER_OUTER_R_MM = ENCODER_OD_MM / 2;

/** Closed circular polyline of `n` samples (first vertex repeated to close). */
function circle(r: number, n: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < n; i++) pts.push(polar(r, (2 * Math.PI * i) / n));
  pts.push(pts[0]);
  return pts;
}

/** SMD pad -> filled rectangle region (axis-aligned w x h at pad center). */
function padRegion(pad: FootprintPad): GerberDraw {
  const [x, y] = pad.at, hx = pad.wMm / 2, hy = pad.hMm / 2;
  return {
    apertureDiaMm: SILK_STROKE_MM, kind: 'region',
    points: [[x - hx, y - hy], [x + hx, y - hy], [x + hx, y + hy], [x - hx, y + hy], [x - hx, y - hy]],
  };
}

// Solder-mask opening over an SMD pad: the pad rectangle expanded by
// MASK_EXPANSION_MM on every side — mirrors PcbStatorComponent.MASK_EXPANSION
// (0.05mm), which isn't exported from that component (pure-module policy: no
// Angular/DI imports here), so the literal is duplicated and documented.
const MASK_EXPANSION_MM = 0.05;

function padMaskRegion(pad: FootprintPad): GerberDraw {
  const [x, y] = pad.at;
  const hx = pad.wMm / 2 + MASK_EXPANSION_MM, hy = pad.hMm / 2 + MASK_EXPANSION_MM;
  return {
    apertureDiaMm: SILK_STROKE_MM, kind: 'region',
    points: [[x - hx, y - hy], [x + hx, y - hy], [x + hx, y + hy], [x - hx, y + hy], [x - hx, y - hy]],
  };
}

function assembleDescriptor(
  bands: EncoderBands, se: ReturnType<typeof buildSensingElement>,
  placements: Footprint[], routes: CoilTrace[], routingVias: Pt[], mounting: MountingHole[], p: EncoderBoardParams,
): BoardDescriptor {
  const w = p.traceWidthMm;

  // F.Cu ('up'): component pads + coil up-bridges + escape routes.
  const fDraws: GerberDraw[] = [];
  for (const fp of placements) {
    for (const pad of fp.pads) {
      if (pad.drillMm !== undefined) fDraws.push({ apertureDiaMm: pad.wMm, kind: 'flash', points: [pad.at] });
      else fDraws.push(padRegion(pad));
    }
  }
  for (const t of se.traces.filter(t => t.layer === 'up')) fDraws.push({ apertureDiaMm: t.widthMm, kind: 'polyline', points: t.points });
  for (const r of routes.filter(r => r.layer === 'up')) fDraws.push({ apertureDiaMm: r.widthMm, kind: 'polyline', points: r.points });

  // B.Cu ('down'): the TX/RX coils + the escape-routing arcs (parked on 'down'
  // so they can never short an 'up' radial jog — see routing comment above).
  const bDraws: GerberDraw[] = [
    ...se.traces.filter(t => t.layer === 'down'),
    ...routes.filter(r => r.layer === 'down'),
  ].map(t => ({ apertureDiaMm: t.widthMm, kind: 'polyline' as const, points: t.points }));

  // Silk: every footprint courtyard.
  const silkDraws: GerberDraw[] = [];
  for (const fp of placements) for (const loop of fp.silk) silkDraws.push({ apertureDiaMm: SILK_STROKE_MM, kind: 'polyline', points: loop });

  // Drills. Plated: coil vias + layer-transition vias at each coil terminal
  // (0.3mm) and header through-holes (1.0mm). NPTH: the shared mounting circle.
  const viaHoles: Pt[] = [
    ...se.vias.map(v => v.at),
    ...se.terminals.map(t => t.at),   // 'up' escape route <-> 'down' coil terminal transition
    ...routingVias,                   // 'up' <-> 'down' at each escape route's ring-arc entry/exit
  ];
  const headerHoles: Pt[] = [];
  for (const fp of placements) for (const pad of fp.pads) if (pad.drillMm !== undefined) headerHoles.push(pad.at);

  // Solder mask (first solderable board on this project — see Task 7 fix
  // report). Every component pad opens on its OWN layer (all placements are
  // 'up' — see the face-assignment note at the top of this module), and every
  // via/hole (coil crossover vias, coil terminal transitions, escape-route
  // ring vias, header through-holes) opens on BOTH sides — a plated hole is
  // exposed copper on whichever side(s) it's drawn on, and every hole here
  // passes clean through both layers. Openings follow the stator's own
  // convention exactly (`PcbStatorComponent.generateSolderMask` /
  // `renderSolderMask`): pad/hole size + MASK_EXPANSION_MM*2, FileFunction
  // `Soldermask,Top`/`Soldermask,Bot`, Negative polarity (mask gerbers draw
  // the OPENINGS, so the file itself is negative-polarity).
  const allHoles = [...viaHoles, ...headerHoles];
  const padMaskDraws: GerberDraw[] = [];
  for (const fp of placements) for (const pad of fp.pads) {
    padMaskDraws.push(pad.drillMm !== undefined
      ? { apertureDiaMm: pad.wMm + MASK_EXPANSION_MM * 2, kind: 'flash', points: [pad.at] }
      : padMaskRegion(pad));
  }
  const fMaskDraws: GerberDraw[] = [
    ...padMaskDraws,
    ...allHoles.map(at => ({ apertureDiaMm: ROUTING_VIA_PAD_MM + MASK_EXPANSION_MM * 2, kind: 'flash' as const, points: [at] })),
  ];
  const bMaskDraws: GerberDraw[] = allHoles.map(at => (
    { apertureDiaMm: ROUTING_VIA_PAD_MM + MASK_EXPANSION_MM * 2, kind: 'flash' as const, points: [at] }
  ));

  const layers: BoardLayerSpec[] = [
    { name: 'F_Cu', fileFunction: 'Copper,L1,Top', polarity: 'Positive', draws: fDraws },
    { name: 'B_Cu', fileFunction: 'Copper,L2,Bot', polarity: 'Positive', draws: bDraws },
    { name: 'F_Mask', fileFunction: 'Soldermask,Top', polarity: 'Negative', draws: fMaskDraws },
    { name: 'B_Mask', fileFunction: 'Soldermask,Bot', polarity: 'Negative', draws: bMaskDraws },
    { name: 'F_Silk', fileFunction: 'Legend,Top', polarity: 'Positive', draws: silkDraws },
  ];

  const drills: DrillSpec[] = [
    { toolDiaMm: 0.3, plated: true, holes: viaHoles },
    { toolDiaMm: 1.0, plated: true, holes: headerHoles },
  ];
  if (mounting.length > 0) {
    drills.push({ toolDiaMm: mounting[0].holeMm, plated: false, holes: mounting.map(h => [h.x, h.y] as Pt) });
  }

  return {
    boardName: 'encoder-ring',
    outline: circle(ENCODER_OUTER_R_MM, OUTLINE_SAMPLES),
    cutouts: [circle(p.boreRadiusMm, OUTLINE_SAMPLES)],
    layers,
    drills,
    x2CopperNames: layers.filter(l => l.fileFunction.startsWith('Copper,')).map(l => l.fileFunction),
  };
}

/** BoardDescriptor for the passive copper target ring (single-sided copper disc
 *  with the key-tab outline and a bore cutout). Consumes the verified
 *  TargetRing geometry directly. */
export function buildTargetBoardDescriptor(target: TargetRing): BoardDescriptor {
  const copper: GerberDraw[] = target.lobes.map(l => ({
    apertureDiaMm: 0.1, kind: 'region' as const, points: l.polygon,
  }));
  const layers: BoardLayerSpec[] = [
    { name: 'F_Cu', fileFunction: 'Copper,L1,Top', polarity: 'Positive', draws: copper },
  ];
  return {
    boardName: 'encoder-target',
    outline: target.outline,
    cutouts: [circle(target.boreRadiusMm, OUTLINE_SAMPLES)],
    layers,
    drills: [],
    x2CopperNames: ['Copper,L1,Top'],
  };
}
